import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { productAPI } from '../../services/api';
import ProductForm from './ProductForm';
import '../Customers/Customers.css';

const ProductListPage = ({ user }) => {
  const navigate = useNavigate();
  const { action, id } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('asc');

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, search, sortBy, sortOrder]);

  // Handle URL-based modal opening
  useEffect(() => {
    if (action === 'new') {
      setIsEditMode(false);
      setSelectedProductId(null);
      setShowFormModal(true);
    } else if (action === 'edit' && id) {
      setIsEditMode(true);
      setSelectedProductId(id);
      setShowFormModal(true);
    } else {
      setShowFormModal(false);
    }
  }, [action, id]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        search: search || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        per_page: 15,
      };
      const response = await productAPI.getAll(params);
      setProducts(response.data);
      setPagination({
        current_page: response.current_page,
        last_page: response.last_page,
        total: response.total,
        per_page: response.per_page,
      });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProducts();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      await productAPI.delete(productId);
      fetchProducts();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete product');
    }
  };

  const handleEdit = (productId) => {
    navigate(`/products/edit/${productId}`);
  };

  const handleNew = () => {
    navigate('/products/new');
  };

  const closeModal = () => {
    navigate('/products');
    setShowFormModal(false);
    setSelectedProductId(null);
  };

  const handleFormSuccess = () => {
    closeModal();
    fetchProducts();
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const getWeightLabel = (weightCode) => {
    const weights = { 1: 'Light', 2: 'Medium', 3: 'Heavy' };
    return weights[weightCode] || weightCode;
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-main-wrapper">
        <main className="dashboard-content">
          <div className="customer-list-container">
            <div className="customer-list-header">
              <h1>Products</h1>
              <button
                className="btn btn-primary"
                onClick={handleNew}
              >
                + Add Product
              </button>
            </div>

            <div className="customer-search">
              <form onSubmit={handleSearch}>
                <input
                  type="text"
                  placeholder="Search by name or code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="search-input"
                />
                <button type="submit" className="btn btn-secondary">
                  Search
                </button>
                {search && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSearch('');
                      setCurrentPage(1);
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>
            </div>

            {error && <div className="error-message">{error}</div>}

            {loading && products.length === 0 ? (
              <div className="loading">Loading products...</div>
            ) : (
              <>
                <div className="customer-table-container">
                  <table className="customer-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('code')} className="sortable">
                          Code {getSortIcon('code')}
                        </th>
                        <th onClick={() => handleSort('description')} className="sortable">
                          Description {getSortIcon('description')}
                        </th>
                        <th onClick={() => handleSort('size')} className="sortable">
                          Size {getSortIcon('size')}
                        </th>
                        <th onClick={() => handleSort('weight_code')} className="sortable">
                          Weight {getSortIcon('weight_code')}
                        </th>
                        <th onClick={() => handleSort('created_at')} className="sortable">
                          Created {getSortIcon('created_at')}
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {products.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="no-data">
                            No products found
                          </td>
                        </tr>
                      ) : (
                        products.map((product) => (
                          <tr key={product.id}>
                            <td data-label="Code">
                              <span className="code-badge">{product.code}</span>
                            </td>
                            <td data-label="Description">{product.description}</td>
                            <td data-label="Size">{product.size}</td>
                            <td data-label="Weight">{getWeightLabel(product.weight_code)}</td>
                            <td data-label="Created">{new Date(product.created_at).toLocaleDateString()}</td>
                            <td data-label="Actions" className="actions-cell">
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => handleEdit(product.id)}
                              >
                                Edit
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDelete(product.id)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {pagination && pagination.last_page > 1 && (
                  <div className="pagination">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span className="pagination-info">
                      Page {pagination.current_page} of {pagination.last_page}
                      ({pagination.total} total)
                    </span>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === pagination.last_page}
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* Form Modal */}
      {showFormModal && (
        <ProductForm
          productId={isEditMode ? selectedProductId : null}
          onClose={closeModal}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default ProductListPage;